import { useMemo } from 'react';

import { CostChannel } from '@sg/graphql-schema';
import { LARGE_PRODUCT_SIZE_THRESHOLD } from '@sg/shared-fe/features/menu';

import { useDynamicFeatureFlags } from '@order/LaunchDarkly';

import {
  type CategoryUpsellsQuery,
  useCategoryUpsellsQuery,
} from '../../screens/BagScreen/GraphQL/CategoryUpsells.generated';
import { useCart } from '../useCart';

// ─────────────────────────────────────────────────────────────────────────────

export const useUpsellsV2 = () => {
  const { cart, cartOrderType } = useCart();
  const restaurantId = cart?.restaurant?.id ?? '';
  const productIdsInCart = useMemo(
    () => cart?.lineItems?.map((item) => item.product.id) ?? [],
    [cart?.lineItems],
  );

  const { numberOfUpsellProducts } = useDynamicFeatureFlags();

  // ─── Upsells Query ────────────────────────────────────────────────────────

  const [response] = useCategoryUpsellsQuery({
    variables: {
      restaurantId,
      costChannel:
        cartOrderType === 'delivery'
          ? CostChannel.DeliveryCostChannel
          : CostChannel.DefaultCostChannel,
    },
    pause: !restaurantId,
    requestPolicy: 'cache-and-network',
  });

  // ────────────────────────────────────────────────────────────

  const upsellProducts = useMemo(() => {
    if (!response.data?.categoryUpsells) {
      return [];
    }

    const firstCategory = response.data.categoryUpsells.find(
      (category) => category.categoryRanking === 1,
    );

    if (!firstCategory) {
      return [];
    }

    const productIdsInCartSet = new Set(productIdsInCart);

    const filteredPopularItems = filterUnwantedProduct(firstCategory.products, productIdsInCartSet);

    return filteredPopularItems.slice(0, numberOfUpsellProducts);
  }, [numberOfUpsellProducts, productIdsInCart, response.data?.categoryUpsells]);

  // ─── Flags ────────────────────────────────────────────────────────────────

  const isLoadingUpsells = response.fetching;
  const maxNumberOfUpsellProducts =
    upsellProducts.length > LARGE_PRODUCT_SIZE_THRESHOLD
      ? numberOfUpsellProducts
      : LARGE_PRODUCT_SIZE_THRESHOLD;

  // ─────────────────────────────────────────────────────────────────────────────

  return {
    upsellProducts,
    isLoadingUpsells,
    maxNumberOfUpsellProducts,
    productSize:
      upsellProducts.length <= LARGE_PRODUCT_SIZE_THRESHOLD
        ? PRODUCT_SIZES.LARGE
        : PRODUCT_SIZES.MEDIUM,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function filterUnwantedProduct(
  upsellProducts: Readonly<UpsellCategoryProduct[]>,
  existingLineItemsProductsIds: Set<string>,
) {
  return upsellProducts.filter(
    (product) => !product.outOfStock && !existingLineItemsProductsIds.has(product.id),
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const PRODUCT_SIZES = {
  LARGE: 'lg',
  MEDIUM: 'md',
} as const;

// ─── Types ──────────────────────────────────────────────────────────────────

type UpsellCategoryProduct = UpsellCategoryObject['products'][number];

type UpsellCategoryObject = NonNullable<CategoryUpsellsQuery['categoryUpsells']>[number];
